import { Component } from 'react';

import { reportClientError } from '../utils/reportClientError';

/*
 * The last thing standing when a render throws.
 *
 * React unmounts the whole tree when a render throws and nothing catches it,
 * which leaves a genuinely blank page -- no message, no navigation, and
 * nothing anywhere saying it happened. This catches that, tells the server,
 * and gives the person something to do.
 *
 * ────────────────────────────────────────────────────────────────────────
 * WHY IT DOES NOT USE useTranslation, OR ANY THEME TOKEN
 * ────────────────────────────────────────────────────────────────────────
 *
 * It has to work when the app does not. i18next is initialised at import
 * time from 37 catalogues, the theme from a stylesheet and a data-theme
 * attribute, and either could be what failed -- a boundary that needs them
 * renders nothing precisely when it is needed. So the two sentences it shows
 * are inline in both languages, chosen from document.documentElement.lang
 * (which i18n/index.js sets, and which the browser leaves alone), and every
 * colour is a literal.
 *
 * It is a class because componentDidCatch has no hook equivalent. That is
 * still true in React 19.
 */

const TEXT = {
  en: {
    dir: 'ltr',
    title: 'Something went wrong on this screen',
    body: 'The rest of the app is fine. Your saved work has not been affected.',
    reload: 'Reload the page',
    home: 'Go to the dashboard',
    reloadTip: 'Load this screen again from the start. Unsaved input on it is lost.',
    homeTip: 'Leave this screen and open the dashboard.',
    reported: 'This has been reported.',
  },
  he: {
    dir: 'rtl',
    title: 'משהו השתבש במסך הזה',
    body: 'שאר המערכת תקינה. העבודה השמורה שלך לא נפגעה.',
    reload: 'רענון הדף',
    home: 'למסך הראשי',
    reloadTip: 'טעינה מחדש של המסך מההתחלה. קלט שלא נשמר בו יאבד.',
    homeTip: 'יציאה מהמסך הזה ומעבר למסך הראשי.',
    reported: 'הדיווח נשלח.',
  },
};

function wording() {
  try {
    return TEXT[document.documentElement.lang] || TEXT.en;
  } catch {
    return TEXT.en;
  }
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false, reported: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    // Nothing in here may throw. This runs while React is already handling a
    // throw, and a second one escaping means the fallback below never
    // renders -- a caught crash turned back into the blank page this whole
    // component exists to prevent.
    //
    // reportClientError already swallows its own failures, so this looks
    // redundant. It is not: `onError` is somebody else's function, and the
    // day reportClientError grows a line outside its try is the day the
    // error screen stops appearing, silently and only in production.
    let reported = false;
    try {
      reported = reportClientError(error, info);
    } catch {
      // reported stays false; the screen below still renders.
    }
    this.setState({ reported });
    try {
      if (this.props.onError) this.props.onError(error, info);
    } catch {
      // A caller's telemetry is not worth the error screen.
    }
  }

  render() {
    if (!this.state.failed) return this.props.children;
    const t = wording();
    return (
      <div
        role="alert"
        data-testid="error-boundary"
        dir={t.dir}
        style={{
          maxWidth: 520, margin: '12vh auto', padding: '28px 26px',
          borderRadius: 14, textAlign: 'center',
          fontFamily: 'system-ui, -apple-system, Segoe UI, Arial, sans-serif',
          // Literals, not tokens: the stylesheet may be what failed to load.
          background: '#ffffff', color: '#1f2937',
          border: '1px solid #e5e7eb', boxShadow: '0 8px 28px rgba(0,0,0,0.12)',
        }}
      >
        <div aria-hidden="true" style={{ fontSize: 34, marginBottom: 10 }}>🍄</div>
        <h1 style={{ fontSize: 19, margin: '0 0 8px' }}>{t.title}</h1>
        <p style={{ fontSize: 14, lineHeight: 1.6, margin: '0 0 18px', color: '#4b5563' }}>
          {t.body}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => window.location.reload()}
            title={t.reloadTip}
            style={{
              padding: '9px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 14,
              border: 'none', background: '#166043', color: '#ffffff',
            }}
          >{t.reload}</button>
          <button
            type="button"
            onClick={() => { window.location.href = '/'; }}
            title={t.homeTip}
            style={{
              padding: '9px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 14,
              border: '1px solid #d1d5db', background: '#ffffff', color: '#1f2937',
            }}
          >{t.home}</button>
        </div>
        {this.state.reported && (
          <p style={{ fontSize: 12, color: '#6b7280', margin: '16px 0 0' }}>{t.reported}</p>
        )}
      </div>
    );
  }
}
